import { IonCard, IonCardContent, IonCardHeader, IonInput, IonSpinner, IonText } from '@ionic/react'
import { AddressElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import strings from 'mobile_web/strings'
import { CreateOrderBody, InAppPurchaseWebPrice, Order, OrderItem } from 'mytours-read-api-typescript'
import { getDirectApi } from 'mytours-read-common'
import { FC, useRef, useState } from 'react'
import { formatCurrency } from '../functions'
import Button from '../../common/components/Button'
import Modal from '../../common/components/modal'

interface Props {
	titleNode: JSX.Element
	webPrice?: InAppPurchaseWebPrice
	itemId: number
	itemType: OrderItem.ItemTypeEnum
	onClose: () => void
}

const PayModal: FC<Props> = ({
	titleNode,
	webPrice: { amount, cents, currency },
	itemId,
	itemType,
	onClose,
}) => {
	const stripe = useStripe()
	const elements = useElements()

	const [address, setAddress] = useState({
		line1: '',
		line2: '',
		city: '',
		state: '',
		postal_code: '',
		country: ''
	  })
	const [email, setEmail] = useState('')
	const [message, setMessage] = useState(null)
	const [isLoading, setIsLoading] = useState(false)

	const initialized = useRef(false)

	if (!stripe || !elements) {
		return null
	}

	if (!initialized.current) {
		const styles = window.getComputedStyle(document.body)
		// We need to set the correct amount before we can collect payment method
		elements.update({ mode: 'payment', amount: cents, currency: currency.toLowerCase(), appearance: {
			variables: {
				fontFamily: styles.getPropertyValue('--ion-font-family'),
				colorText: styles.getPropertyValue('--body-color'),
				colorBackground: styles.getPropertyValue('--background-color')
			}
		} })
		initialized.current = true
	}

	const price = formatCurrency(currency, amount)

	const handleChange = (e: CustomEvent<any>): void => {
		setEmail((e.target as HTMLInputElement).value)
	}

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()

		setIsLoading(true)

		// Trigger form validation and wallet collection
		const submit = await elements.submit()
		if (submit.error) {
			setMessage(submit.error.message)
			setIsLoading(false)
			return
		}

		let order: Order
		try {
			order = await getDirectApi().orderCreate({
				order_type: CreateOrderBody.OrderTypeEnum.Stripe,
				email,
				items: [{
					item_type: itemType,
					item_id: itemId,
				}],
				address
			})
		} catch (error) {
			setMessage('An unexpected error occurred.')
			setIsLoading(false)
			return
		}
		const clientSecret = order.stripe_payment_intent_client_secret

		if (order.amount !== amount || order.currency !== currency) {
			setMessage('No payment has been collected yet. The price has been updated to ' + formatCurrency(order.currency, Number(order.amount)) + '. please refresh the page to try again.')
			return
		}

		const redirectUrl = new URL(window.location.href)
		redirectUrl.searchParams.set('order-id', String(order.id))
		const { error } = await stripe.confirmPayment({
			elements,
			clientSecret,
			confirmParams: {
				return_url: redirectUrl.toString(),
				receipt_email: email,
			},
		})

		// A successful payment will redirect to the order page and never reach this point
		// Stripe suggests to show the error message of these 2 specific types to the user
		if (error.type === "card_error" || error.type === "validation_error") {
			setMessage(error.message)
		} else {
			setMessage("An unexpected error occurred.")
		}

		setIsLoading(false)
	}

	return (
		<Modal isOpen={true} backdropDismiss={false} cssClass="pay-modal">
			<IonCard>
				<IonCardHeader>
					<Button className="close" onClick={onClose} ariaLabel={strings.close}>
						<i className="fal fa-xmark" />
					</Button>
				</IonCardHeader>
				<IonCardContent className="pay-modal-content">
					{titleNode}
					<h3>{price}</h3>
					<form onSubmit={handleSubmit}>
						<label htmlFor="email">{strings.email_label}</label>
						<IonInput value={email} onIonChange={handleChange} id="email" type="email" required className="email-input" />

						<AddressElement
							options={{ mode: 'billing', fields: { phone: 'never' } }}
							onChange={event => setAddress(event.value.address)}
						/>

						<PaymentElement onLoadError={({ error }) => setMessage(error.message)} />

						{message && <IonText className="error-message">{message}</IonText>}

						<Button fill="clear" ariaLabel={strings.pay_now} className="-submit" type="submit" disabled={isLoading || !stripe || !elements}>	
							{isLoading ? <IonSpinner name="crescent" /> : strings.pay_now}
						</Button>
					</form>
				</IonCardContent>
			</IonCard>
		</Modal>
	)
}

export default PayModal
